import { Link } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import { ChevronRightIcon, QuestionCircleIcon } from '../../components/icons';
import { HeaderNotificationsLink } from './HeaderNotificationsLink';
import { TECHDOCS_HEADER_SLOT_ID } from './techDocsHeaderSlot';

const useStyles = makeStyles({
  root: {
    flex: 'none',
    height: 'var(--hone-portal-header-height)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '0 28px',
    background: 'var(--bui-white)',
    borderBottom: '1px solid var(--hone-portal-hairline-soft)',
  },
  crumbRoot: {
    fontSize: 12.5,
    color: 'var(--bui-fg-secondary)',
    textDecoration: 'none',
    '&:hover': { color: 'var(--bui-fg-primary)' },
  },
  chevron: {
    width: 12,
    height: 12,
    opacity: 0.4,
  },
  crumbCurrent: {
    fontSize: 12.5,
    fontWeight: 500,
    color: 'var(--bui-fg-primary)',
  },
  spacer: { flex: 1 },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  actionIcon: {
    width: 16,
    height: 16,
    opacity: 0.5,
    display: 'block',
  },
  action: {
    display: 'inline-flex',
    lineHeight: 0,
  },
});

// First path segment -> breadcrumb label. Falls back to a title-cased segment,
// so newly added routes still read sensibly without a map entry.
export const LABELS: Record<string, string> = {
  '': 'Home',
  catalog: 'Catalog',
  documentation: 'Documentation',
  'catalog-graph': 'Catalog Graph',
  'catalog-import': 'Register',
  announcements: 'Announcements',
  feedback: 'Feedback',
  search: 'Search',
  settings: 'Settings',
  'admin-settings': 'Admin Settings',
  notifications: 'Notifications',
  'api-docs': 'APIs',
  rbac: 'RBAC',
  pagerduty: 'PagerDuty',
};

export function crumbForPath(pathname: string): string {
  const seg = pathname.split('/').filter(Boolean)[0] ?? '';
  return (
    LABELS[seg] ?? seg.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  );
}

// The persistent 52px breadcrumb/actions bar at the top of the content column.
// Page titles live in the page body, not here (mock 1b), so this stays a thin
// breadcrumb strip plus right-aligned help + notifications affordances.
export function PortalHeader() {
  const classes = useStyles();
  const { pathname } = useLocation();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.crumbRoot}>
        Hone Portal
      </Link>
      <ChevronRightIcon className={classes.chevron} />
      <span className={classes.crumbCurrent}>{crumbForPath(pathname)}</span>
      <span className={classes.spacer} />
      <span className={classes.actions}>
        {/*
         * Empty until the TechDocs reader is on screen. `ReaderSettingsAddon`
         * portals its trigger + menu in here — see `techDocsHeaderSlot.ts` —
         * so the reader's settings control reads as one of this cluster's own
         * icons rather than a control floating over the page.
         */}
        <span id={TECHDOCS_HEADER_SLOT_ID} />
        {/* No dedicated help route exists; the portal's own documentation is
            the help surface, so "?" lands on the TechDocs index. */}
        <Link
          to="/documentation"
          underline="none"
          title="Help and documentation"
          aria-label="Help"
          className={classes.action}
        >
          <QuestionCircleIcon className={classes.actionIcon} />
        </Link>
        <HeaderNotificationsLink />
      </span>
    </div>
  );
}
